/**
 * Mixins for Marionette Views
 */
import accounting from "accounting";
import { abbreviateNumber } from "common/formats";
import { store } from "common/store";
import { _ } from "extensions/underscore";

import { watch } from "common/components/watch";

export const ViewMixin = {
    /**
     * Arm the collection to react on currency picker changes
     * @param {Object} options Method options
     */
    setupCurrencyPicker(options) {
        if (!options.structure) {
            throw new Error("Structure is required for this call");
        }
        
        const state = store.getState();
        
        // Set current value
        // $FlowFixMe: flow cannot call setQueryParam from mixed type here. Create parameterized model/collection type
        options.structure.setQueryParam({
            currency: state.common.currency
        });
        
        // Subscribe to future changes from the redux store
        const subscriptions = [{
            objectPath: "common.currency"
        }];
    
        this._reduxSubscriptions = [
            ...subscriptions.map(({ objectPath, compare }) => {
                const w = watch(store.getState, objectPath, compare);
                return store.subscribe(w((newCurrency) => {
                    options.structure.getForCurrency(newCurrency);
                }));
            }),
            ...this._reduxSubscriptions || []
        ];
    },
    
    /**
     * Configure viewMixin for global datepicker
     * @param {Object} [options] Setup options
     * @param {Backbone.Model | Backbone.Collection} options.structure Structure to watch for currency datepicker changes
     */
    setupDatePicker({ structure }) {
        const subscriptions = [
            {
                objectPath: "common.date_range.startDate"
            },
            {
                objectPath: "common.date_range.endDate"
            },
            {
                objectPath: "common.date_range.datetype"
            }
        ];
        
        const fetchDebounced = _.debounce(() => {
            const state = store.getState();
            
            // Return earlier if there are no dates to set
            if (!state.common.date_range.startDate || !state.common.date_range.endDate) return;
    
            const params = {
                start: state.common.date_range.startDate,
                end: state.common.date_range.endDate
            };
    
            if (state.common.date_range.datetype) {
                params.datetype = state.common.date_range.datetype;
            }
            
            structure.getForDateRange(params);
        }, 50);
        
        this._reduxSubscriptions = [
            ...subscriptions.map(({ objectPath, compare }) => {
                const w = watch(store.getState, objectPath, compare);
                return store.subscribe(w(fetchDebounced));
            }),
            ...this._reduxSubscriptions || []
        ];
        
        // Set initial params
        fetchDebounced();
    },
    
    getCurrentDatePickerValues() {
        const state = store.getState();
        
        return {
            start: state.common.date_range.startDate,
            end: state.common.date_range.endDate
        };
    },
    
    getCurrentCurrency() {
        const state = store.getState();
        
        return {
            currency: state.common.currency,
            decimals: state.common.currency_decimals,
            symbol: state.common.currency_symbol
        };
    },
    
    /**
     * Format provided number into amount with default currency
     * @param {number | string} n number to convert to amount
     * @param {object} options override default options
     * @return {string} amount
     */
    formatAmountWithCurrentCurrency(n, options) {
        // Don't format hyphens
        if (n === "-") {
            return "-";
        }
        
        const currency = this.getCurrentCurrency();
        const state = store.getState();
        const opts = {
            format: state.config.formatting.money.format,
            precision: state.config.formatting.money.precision,
            symbol: currency.symbol,
            abbreviate: false,
            ...options
        };
        
        // Allow to pick either a symbol or currency
        if (typeof opts.symbol === "function") {
            opts.symbol = opts.symbol(currency);
        }
        
        // Check if number is less than minimum precision
        const minAllowedValueByPrecision = 10 ** -opts.precision;
        const ridiculouslySmallValue = 1e-10; // 0.0000000001
        
        // Make sure that we don't show less-than if the value is a very small float (e.g. 2.220446049250313e-15)
        if (n > 0 && n < minAllowedValueByPrecision && ridiculouslySmallValue < n) {
            opts.format.pos = `&lt; ${opts.format.pos}`;
            opts.format.neg = `&lt; ${opts.format.neg}`;
            n = minAllowedValueByPrecision;
        } else if (opts.abbreviate) {
            n = abbreviateNumber(n, true);
            
            return `${n.prefix}${opts.symbol}${n.value}${n.suffix}`;
        }
        
        return accounting.formatMoney(n, opts);
    },
    
    /**
     * Format Number to User-friendly number string
     * @param {number} n number to format
     * @param {number} [precision=0] formatting precision
     * @returns {string}
     */
    formatNumber(n, precision) {
        return accounting.formatNumber(n, precision);
    },
    
    rerenderOnEntityChange(dataStructure) {
        this.onEntityChange(() => {
            dataStructure.fetch();
        });
    },
    
    onEntityChange(cb) {
        this._reduxSubscriptions = [
            store.subscribe(watch(store.getState, "common.bu_ref")((new_bu_ref) => {
                cb.call(this, new_bu_ref);
            })),
            ...this._reduxSubscriptions || []
        ];
    }
};
